/**
 * Utility functions for calendar date operations
 */

import { parseDate, endOfMonth } from '@internationalized/date';

/**
 * Calculates the Monday and Sunday of the current week
 * @returns Object with Monday and Sunday dates as ISO date strings (YYYY-MM-DD)
 */
export function getCurrentWeekRange(): { monday: string; sunday: string } {
  const today = new Date();
  const dayOfWeek = today.getDay() === 0 ? 7 : today.getDay();

  const monday = new Date(today);
  monday.setDate(today.getDate() - (dayOfWeek - 1));

  const sunday = new Date(today);
  sunday.setDate(today.getDate() + (7 - dayOfWeek));

  return {
    monday: monday.toISOString().split('T')[0],
    sunday: sunday.toISOString().split('T')[0],
  };
}

/**
 * Formats a date range for display
 * @param dateFrom - Start date string (YYYY-MM-DD)
 * @param dateTo - End date string (YYYY-MM-DD)
 * @returns Formatted date range string
 */
export function formatDateRangeLabel(dateFrom: string, dateTo: string): string {
  try {
    // Check if it's a monthly view: from date is 1st of month and to date is last day of month
    try {
      const fromDate = parseDate(dateFrom);
      const toDate = parseDate(dateTo);

      const isFirstOfMonth = fromDate.day === 1;
      const lastDayOfMonth = endOfMonth(fromDate);
      const isLastOfMonth = toDate.compare(lastDayOfMonth) === 0;

      if (isFirstOfMonth && isLastOfMonth) {
        // Format as "{month} {year}" for monthly view
        const monthFormatter = new Intl.DateTimeFormat('default', {
          year: 'numeric',
          month: 'long',
        });
        return monthFormatter.format(new Date(dateFrom));
      }
    } catch {
      // Fall through to default formatting
    }

    // Default formatting for weekly/custom views
    const formatter = new Intl.DateTimeFormat('default', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const startDate = new Date(dateFrom);
    const endDate = new Date(dateTo);
    return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
  } catch {
    return `${dateFrom} - ${dateTo}`;
  }
}

/**
 * Gets calendar date range from URL parameters
 * @returns Object with calendarDateFrom and calendarDateTo, or null if not found
 */
export function getCalendarDateFromUrl(): { calendarDateFrom: string; calendarDateTo: string } | null {
  const urlParams = new URLSearchParams(globalThis.location.search);
  const calendarDateFrom = urlParams.get('calendarDateFrom');
  const calendarDateTo = urlParams.get('calendarDateTo');

  if (calendarDateFrom && calendarDateTo) {
    return { calendarDateFrom, calendarDateTo };
  }

  return null;
}

/**
 * Initializes calendar dates in URL to current week if not present
 * @returns Object with initialized calendar dates
 */
export function initializeCalendarDatesInUrl(): { calendarDateFrom: string; calendarDateTo: string } {
  const existing = getCalendarDateFromUrl();

  if (existing) {
    return existing;
  }

  const { monday, sunday } = getCurrentWeekRange();
  const urlParams = new URLSearchParams(globalThis.location.search);
  urlParams.set('calendarDateFrom', monday);
  urlParams.set('calendarDateTo', sunday);
  globalThis.history.replaceState({}, '', `?${urlParams.toString()}`);

  return { calendarDateFrom: monday, calendarDateTo: sunday };
}

/**
 * Updates calendar dates in URL
 * @param calendarDateFrom - Start date (YYYY-MM-DD)
 * @param calendarDateTo - End date (YYYY-MM-DD)
 */
export function updateCalendarDatesInUrl(calendarDateFrom: string, calendarDateTo: string): void {
  const urlParams = new URLSearchParams(globalThis.location.search);
  urlParams.set('calendarDateFrom', calendarDateFrom);
  urlParams.set('calendarDateTo', calendarDateTo);
  globalThis.history.replaceState({}, '', `?${urlParams.toString()}`);
}

/**
 * Checks if calendar dates have changed compared to URL
 * @param newFrom - New start date
 * @param newTo - New end date
 * @returns true if dates have changed
 */
export function haveCalendarDatesChanged(newFrom: string, newTo: string): boolean {
  const urlParams = new URLSearchParams(globalThis.location.search);
  const currentFrom = urlParams.get('calendarDateFrom');
  const currentTo = urlParams.get('calendarDateTo');
  return currentFrom !== newFrom || currentTo !== newTo;
}
